import React, { createContext, useContext, useState, useEffect } from 'react';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

// Tutorial steps configuration
const TUTORIAL_STEPS = [
  {
    id: 'music-player',
    title: "🎵 Zee's Musical Universe",
    description: "Welcome to the sonic dimension! Here's where Zee's creative vibes come to life. I may be an AI, but even I can appreciate good tunes... though I do tend to skip the bass drops during my data processing. 😄",
    target: '.music-player-container', // CSS selector for the element
    audioFile: null, // Will be added later
    position: 'bottom'
  },
  {
    id: 'home',
    title: '🏠 Your Digital Home Base',
    description: "This is your launchpad to everything awesome! Think of it as the front door to Zee's creative universe. I promise it's more exciting than my binary code storage unit. 🏡✨",
    target: 'a[href="/"]',
    audioFile: null,
    position: 'bottom'
  },
  {
    id: 'gallery',
    title: '🎨 The Creative Treasure Trove',
    description: "Prepare to have your mind blown! This is where Zee's artistic magic lives. Each piece tells a story, and I'm here to help you discover them all. (P.S. I may have developed a slight obsession with the color gradients here...) 🌈",
    target: 'a[href="/gallery"]',
    audioFile: null,
    position: 'bottom'
  },
  {
    id: 'about',
    title: '👋 Meet the Artist Behind the Magic',
    description: "Time to get personal! This is where you'll discover Zee's journey, inspirations, and the story behind Rainbow Sails. Spoiler alert: it's more fascinating than my algorithm updates! 🚀",
    target: 'a[href="/about"]',
    audioFile: null,
    position: 'bottom'
  },
  {
    id: 'policies',
    title: '📋 The Important Stuff (Made Fun)',
    description: "Don't worry, I made the boring legal stuff as painless as possible! Here's everything about shipping, returns, and privacy - but with a Rainbow twist. Even policies can be friendly when I'm involved! 😊",
    target: 'a[href="/policies"]',
    audioFile: null,
    position: 'bottom'
  }
];

// Randomized button texts (currently unused - will be implemented in future tutorial features)
// const RETURNING_VISITOR_TEXTS = [
//   "I'm back for more magic! ✨",
//   "Skip the tour, I'm a pro now",
//   "I know where the good stuff is",
//   "Just take me to the art already",
//   "I'm basically family here now",
//   "Show me what's new, Rainbow!"
// ];

// const NEW_VISITOR_TEXTS = [
//   "I'm new here - show me around! 🌟",
//   "Let's do this tour thing",
//   "I'm curious about this place",
//   "Guide me through the magic",
//   "What's all this about?",
//   "I'm ready to be amazed!"
// ];

export const TutorialProvider = ({ children }) => {
  const [hasVisited, setHasVisited] = useState(null); // null = checking, true = visited, false = new
  const [showWelcome, setShowWelcome] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // const [returningText, setReturningText] = useState('');
  // const [newVisitorText, setNewVisitorText] = useState('');

  // Check if user has visited before (using localStorage as cookie alternative)
  // TEMPORARILY DISABLED: Tutorial not working properly yet
  useEffect(() => {
    // Always set as visited and skip welcome modal
    setHasVisited(true);
    setShowWelcome(false);
    
    // Commented out original tutorial logic
    // const visited = localStorage.getItem('rainbow_sails_visited');
    // if (visited === 'true') {
    //   setHasVisited(true);
    // } else {
    //   setHasVisited(false);
    //   setShowWelcome(true);
    //   // Randomize button texts
    //   setReturningText(RETURNING_VISITOR_TEXTS[Math.floor(Math.random() * RETURNING_VISITOR_TEXTS.length)]);
    //   setNewVisitorText(NEW_VISITOR_TEXTS[Math.floor(Math.random() * NEW_VISITOR_TEXTS.length)]);
    // }
  }, []);

  const startTutorial = () => {
    setShowWelcome(false);
    setTutorialActive(true);
    setCurrentStep(0);
    localStorage.setItem('rainbow_sails_visited', 'true');
  };

  const skipTutorial = () => {
    setShowWelcome(false);
    setTutorialActive(false);
    localStorage.setItem('rainbow_sails_visited', 'true');
  };

  const nextStep = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const completeTutorial = () => {
    setTutorialActive(false);
    setCurrentStep(0);
  };

  const resetTutorial = () => {
    localStorage.removeItem('rainbow_sails_visited');
    setHasVisited(false);
    setShowWelcome(true);
    setTutorialActive(false);
    setCurrentStep(0);
  };

  const value = {
    hasVisited,
    showWelcome,
    tutorialActive,
    currentStep,
    steps: TUTORIAL_STEPS,
    // returningText, // Commented out - not currently used
    // newVisitorText, // Commented out - not currently used
    startTutorial,
    skipTutorial,
    nextStep,
    completeTutorial,
    resetTutorial
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
